// composables/useGeolocation.ts
import { ref, onUnmounted } from 'vue';
import type { Ref } from 'vue';

export interface GeolocationCoordinates {
    readonly latitude: number;
    readonly longitude: number;
    readonly altitude: number | null;
    readonly accuracy: number;
    readonly altitudeAccuracy: number | null;
    readonly heading: number | null;
    readonly speed: number | null;
}

export interface GeolocationPosition {
    readonly coords: GeolocationCoordinates;
    readonly timestamp: number;
}

// Usamos el tipo nativo GeolocationPositionError del navegador
export type GeolocationError = GeolocationPositionError;

export interface UseGeolocationOptions extends PositionOptions {
    immediate?: boolean;
    tracking?: boolean;
}

export interface UseGeolocationReturn {
    position: Ref<GeolocationPosition | null>;
    error: Ref<string | null>;
    acquiring: Ref<boolean>;
    tracking: Ref<boolean>;
    supportsGeolocation: Ref<boolean>;
    getCurrentPosition: () => Promise<void>;
    startTracking: () => void;
    stopTracking: () => void;
    clearPosition: () => void;
    clearError: () => void;
    calculateDistance: (lat2: number, lon2: number, unit?: 'km' | 'mi') => number | null;
    formatPosition: (format?: 'dd' | 'dms') => string | null;
    isUsingGPS: () => boolean | null;
}

/**
 * Composable para manejar la geolocalización en Vue 3 con TypeScript
 * @param options - Opciones de configuración para la geolocalización
 * @returns Funciones y estados para manejar la geolocalización
 */
export function useGeolocation(options: UseGeolocationOptions = {}): UseGeolocationReturn {
    const {
        enableHighAccuracy = true,
        timeout = 15000,     // Aumentado para Safari iOS
        maximumAge = 0, // sin caché
        immediate = false,
        tracking = false
    } = options;

    const position = ref<GeolocationPosition | null>(null);
    const error = ref<string | null>(null);
    const acquiring = ref<boolean>(false);
    const trackingState = ref<boolean>(tracking);
    const supportsGeolocation = ref<boolean>(!!navigator.geolocation);
    let watchId: number | null = null;

    const defaultOptions: PositionOptions = {
        enableHighAccuracy,
        timeout,
        maximumAge
    };

    const clearPosition = (): void => {
        position.value = null;
    };

    const clearError = (): void => {
        error.value = null;
    };

    const handleGeolocationError = (err: GeolocationPositionError): void => {
        let errorMessage = 'Error desconocido';

        switch (err.code) {
            case 1: // PERMISSION_DENIED
                errorMessage = 'Permiso de ubicación denegado';
                break;
            case 2: // POSITION_UNAVAILABLE
                errorMessage = 'Información de ubicación no disponible';
                break;
            case 3: // TIMEOUT
                errorMessage = 'Tiempo de espera de ubicación agotado';
                break;
        }

        error.value = errorMessage;
        acquiring.value = false;
        trackingState.value = false;
    };

    /**
     * Obtiene la posición actual una vez
     * @returns Promise que se resuelve cuando se obtiene la posición
     */
    const getCurrentPosition = (): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (!supportsGeolocation.value) {
                error.value = 'Geolocalización no es soportada por este navegador';
                reject(new Error('Geolocalización no soportada'));
                return;
            }

            acquiring.value = true;

            navigator.geolocation.getCurrentPosition(
                (pos: GeolocationPosition) => {
                    position.value = pos;
                    error.value = null;
                    acquiring.value = false;
                    resolve();
                },
                (err: GeolocationPositionError) => {
                    handleGeolocationError(err);
                    reject(err);
                },
                defaultOptions
            );
        });
    };

    /**
     * Inicia el seguimiento continuo de la posición
     */
    const startTracking = (): void => {
        if (!supportsGeolocation.value) {
            error.value = 'Geolocalización no es soportada por este navegador';
            return;
        }

        if (trackingState.value) {
            return;
        }

        trackingState.value = true;

        watchId = navigator.geolocation.watchPosition(
            (pos: GeolocationPosition) => {
                position.value = pos;
                error.value = null;
            },
            (err: GeolocationPositionError) => {
                handleGeolocationError(err);
            },
            defaultOptions
        );
    };

    /**
     * Detiene el seguimiento continuo de la posición
     */
    const stopTracking = (): void => {
        if (watchId !== null) {
            navigator.geolocation.clearWatch(watchId);
            watchId = null;
        }
        trackingState.value = false;
    };

    /**
     * Calcula la distancia entre la posición actual y unas coordenadas dadas
     * @param lat2 - Latitud del punto destino
     * @param lon2 - Longitud del punto destino
     * @param unit - Unidad de medida ('km' para kilómetros, 'mi' para millas)
     * @returns Distancia en la unidad especificada o null si no hay posición actual
     */
    const calculateDistance = (lat2: number, lon2: number, unit: 'km' | 'mi' = 'km'): number | null => {
        if (!position.value) return null;

        const { latitude: lat1, longitude: lon1 } = position.value.coords;

        const R = unit === 'km' ? 6371 : 3956; // Radio de la Tierra en km o millas
        const dLat = deg2rad(lat2 - lat1);
        const dLon = deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = R * c;

        return Number(distance.toFixed(2));
    };

    const deg2rad = (deg: number): number => {
        return deg * (Math.PI / 180);
    };

    /**
     * Formatea la posición actual en diferentes formatos
     * @param format - Formato de salida ('dd' para grados decimales, 'dms' para grados, minutos, segundos)
     * @returns Cadena formateada con la posición o null si no hay posición actual
     */
    const formatPosition = (format: 'dd' | 'dms' = 'dd'): string | null => {
        if (!position.value) return null;

        const { latitude, longitude } = position.value.coords;

        if (format === 'dd') {
            return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        } else {
            const latDirection = latitude >= 0 ? 'N' : 'S';
            const lonDirection = longitude >= 0 ? 'E' : 'W';

            const latDegrees = Math.floor(Math.abs(latitude));
            const latMinutes = Math.floor((Math.abs(latitude) - latDegrees) * 60);
            const latSeconds = ((Math.abs(latitude) - latDegrees - latMinutes / 60) * 3600).toFixed(1);

            const lonDegrees = Math.floor(Math.abs(longitude));
            const lonMinutes = Math.floor((Math.abs(longitude) - lonDegrees) * 60);
            const lonSeconds = ((Math.abs(longitude) - lonDegrees - lonMinutes / 60) * 3600).toFixed(1);

            return `${latDegrees}°${latMinutes}'${latSeconds}"${latDirection} ${lonDegrees}°${lonMinutes}'${lonSeconds}"${lonDirection}`;
        }
    };

    /**
     * Detecta si está usando GPS basándose en la precisión de la ubicación
     * @returns true si probablemente está usando GPS, false si está usando otra fuente, null si no hay posición
     */
    const isUsingGPS = (): boolean | null => {
        if (!position.value) return null;

        const { accuracy, altitudeAccuracy } = position.value.coords;

        // GPS típicamente proporciona:
        // - Precisión horizontal < 20 metros (usualmente 3-10m)
        // - Precisión de altitud disponible
        // WiFi/Cellular típicamente proporciona:
        // - Precisión horizontal 50-500+ metros
        // - Sin precisión de altitud o muy imprecisa

        const hasHighAccuracy = accuracy < 20; // Menos de 20 metros
        const hasAltitudeAccuracy = altitudeAccuracy !== null && altitudeAccuracy < 50;

        // Si tiene alta precisión horizontal O precisión de altitud, probablemente es GPS
        return hasHighAccuracy || hasAltitudeAccuracy;
    };

    // Iniciar automáticamente si se especificó immediate
    if (immediate && supportsGeolocation.value) {
        getCurrentPosition().catch(() => {
            // Error manejado por la función
        });
    }

    // Iniciar seguimiento automáticamente si se especificó tracking
    if (tracking && supportsGeolocation.value) {
        startTracking();
    }

    // Limpiar el watch al desmontar el componente
    onUnmounted(() => {
        stopTracking();
    });

    return {
        position,
        error,
        acquiring,
        tracking: trackingState,
        supportsGeolocation,
        getCurrentPosition,
        startTracking,
        stopTracking,
        clearPosition,
        clearError,
        calculateDistance,
        formatPosition,
        isUsingGPS
    };
}

